import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import InvoiceNumberingSettings from './InvoiceNumberingSettings';
import WFirmaIntegrationSettings from './WFirmaIntegrationSettings';
import CompanyUsersTab from './CompanyUsersTab';
import CompanyApiKeysSettings from './CompanyApiKeysSettings';
import { useTranslation } from '../hooks/useTranslation';
import { counterpartyService } from '../services/counterpartyService';

interface CompanySettingsTabProps {
  companyId: string;
}

const CompanySettingsTab: React.FC<CompanySettingsTabProps> = ({ companyId }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [activeSection, setActiveSection] = useState<'numbering' | 'integrations' | 'apiKeys' | 'users'>('numbering');
  const [bulkSyncStatus, setBulkSyncStatus] = useState<'idle' | 'syncing' | 'unsyncing' | 'success' | 'error'>('idle');
  const [bulkSyncMessage, setBulkSyncMessage] = useState('');

  const unsyncAllMutation = useMutation({
    mutationFn: () => counterpartyService.unsyncAllCounterpartiesFromWFirma(companyId),
    onMutate: () => {
      setBulkSyncStatus('unsyncing');
      setBulkSyncMessage('');
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['counterparties', companyId] });
      setBulkSyncStatus('success');
      setBulkSyncMessage(data?.message || t('companies.counterparties.unsyncAllSuccess') || 'Привязка сброшена');
      setTimeout(() => { setBulkSyncStatus('idle'); setBulkSyncMessage(''); }, 3000);
    },
    onError: (error: any) => {
      setBulkSyncStatus('error');
      setBulkSyncMessage(error?.response?.data?.message || error?.message || 'Ошибка');
      setTimeout(() => { setBulkSyncStatus('idle'); setBulkSyncMessage(''); }, 5000);
    }
  });

  const syncAllMutation = useMutation({
    mutationFn: () => counterpartyService.syncAllCounterpartiesToWFirma(companyId),
    onMutate: () => {
      setBulkSyncStatus('syncing');
      setBulkSyncMessage('');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['counterparties', companyId] });
      setBulkSyncStatus('success');
      setBulkSyncMessage(t('companies.counterparties.syncAllSuccess') || 'Все контрагенты синхронизированы');
      setTimeout(() => { setBulkSyncStatus('idle'); setBulkSyncMessage(''); }, 3000);
    },
    onError: (error: any) => {
      setBulkSyncStatus('error');
      setBulkSyncMessage(error?.response?.data?.message || error?.message || 'Ошибка');
      setTimeout(() => { setBulkSyncStatus('idle'); setBulkSyncMessage(''); }, 5000);
    }
  });

  const sections = [
    { id: 'numbering' as const, label: t('settings.sections.invoiceNumbering') || 'Нумерация фактур', icon: '🔢' },
    { id: 'integrations' as const, label: t('settings.sections.integrations') || 'Интеграции', icon: '🔌' },
    { id: 'apiKeys' as const, label: t('settings.sections.apiKeys') || 'API keys', icon: '🔑' },
    { id: 'users' as const, label: t('companies.tabs.users') || 'Пользователи', icon: '👤' }
  ];

  return (
    <div className="space-y-6">
      {/* Навигация по секциям */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeSection === section.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{section.icon}</span>
                {section.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Контент секций */}
      <div className="space-y-6">
        {activeSection === 'numbering' && (
          <InvoiceNumberingSettings companyId={companyId} />
        )}
        
        {activeSection === 'integrations' && (
          <>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex flex-col gap-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('companies.counterparties.syncConfirmTitle') || 'Синхронизация контрагентов с wFirma'}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('companies.counterparties.syncAllHint') || 'Добавить всех контрагентов в wFirma'}
                  {' / '}
                  {t('companies.counterparties.unsyncAllHint') || 'Сбросить привязку к старой wFirma'}
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => window.confirm(t('companies.counterparties.unsyncAllConfirm') || 'Сбросить привязку к wFirma у всех контрагентов? После этого можно будет заново синхронизировать с новой учётной записью wFirma.') && unsyncAllMutation.mutate()}
                    disabled={unsyncAllMutation.isPending || syncAllMutation.isPending}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                    title={t('companies.counterparties.unsyncAllHint') || 'Отвязать всех от старой wFirma'}
                  >
                    {unsyncAllMutation.isPending ? (t('common.loading') || '...') : (t('companies.counterparties.unsyncAll') || 'Отвязать от wFirma')}
                  </button>
                  <button
                    onClick={() => syncAllMutation.mutate()}
                    disabled={unsyncAllMutation.isPending || syncAllMutation.isPending}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                    title={t('companies.counterparties.syncAllHint') || 'Добавить всех контрагентов в wFirma'}
                  >
                    {syncAllMutation.isPending ? (t('common.loading') || '...') : (t('companies.counterparties.syncAll') || 'Синхр. в wFirma')}
                  </button>
                </div>
                {(bulkSyncStatus === 'success' || bulkSyncStatus === 'error') && (
                  <p className={`text-sm ${bulkSyncStatus === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                    {bulkSyncMessage}
                  </p>
                )}
              </div>
            </div>
            <WFirmaIntegrationSettings companyId={companyId} />
          </>
        )}

        {activeSection === 'apiKeys' && (
          <CompanyApiKeysSettings companyId={companyId} />
        )}
        
        {activeSection === 'users' && (
          <CompanyUsersTab companyId={companyId} />
        )}
      </div>
    </div>
  );
};

export default CompanySettingsTab;
